import React, { useState, useEffect, useMemo } from 'react';
import { Edit, Plus } from 'lucide-react';
import FormModal from './FormModal';
import { usersApi } from '@domains/admin/api/users.api';
import { useAuth } from '@contexts/AuthContext';

// Permission profiles will be loaded dynamically from API

interface AdminUserModalProps {
  onClose: () => void;
  onSubmit: (user: any) => void | Promise<void>;
  selectedUser: any;
  availableCompanies: any[];
  availablePartners?: { id: number; name: string }[];
  availablePermissions?: string[]; // For advanced permission overrides
  hideCompanySelection?: boolean; // Hide company selection when already in a company context
  companyScoped?: boolean; // Only show company-scoped profiles (hide System Admin / External Auditor)
  readOnly?: boolean; // New prop to make the modal read-only
  size?: 'small' | 'medium' | 'large';
  onRefreshProfiles?: () => void; // Callback to refresh permission profiles
}

export const AdminUserModal: React.FC<AdminUserModalProps> = ({
  onClose,
  onSubmit,
  selectedUser,
  availableCompanies,
  availablePartners = [],
  availablePermissions = [],
  hideCompanySelection = false,
  companyScoped = false,
  readOnly = false,
  size = 'large',
  onRefreshProfiles,
}) => {
  const { user: currentUser } = useAuth();
  const isSystemAdmin = !currentUser?.company_id;
  const [user, setUser] = useState(
    selectedUser || {
      id: null,
      first_name: '',
      last_name: '',
      email: '',
      user_types: [] as number[], // User can belong to multiple permission profiles
      individualPermissions: [] as string[], // Individual permission overrides
      company_id: '',
      partner_id: null as number | null,
      is_active: true,
    }
  );
  const [selectedUserTypes, setSelectedUserTypes] = useState<number[]>([]);
  const [availableUserTypes, setAvailableUserTypes] = useState<any[]>([]);
  const [companySearchTerm, setCompanySearchTerm] = useState('');
  const [showCompanyDropdown, setShowCompanyDropdown] = useState(false);

  // Load available permission profiles
  useEffect(() => {
    const loadUserTypes = async () => {
      try {
        if (onRefreshProfiles) {
          await onRefreshProfiles();
        }
        const userTypes = await usersApi.getUserTypes(true);
        // When opened by a Company Admin (companyScoped or has company_id),
        // only show roles they can actually assign to their employees.
        // External Auditor = system-level, Partner Admin = platform-level,
        // System Admin = platform-level. None are company-manageable.
        const COMPANY_ROLES = new Set(['Company Admin', 'Compliance User', 'Internal Auditor']);
        const filtered = (companyScoped || currentUser?.company_id)
          ? userTypes.filter((t: any) => COMPANY_ROLES.has(String(t.name)))
          : userTypes;
        setAvailableUserTypes(filtered);
      } catch (error) {
        console.error('Error loading permission profiles:', error);
      }
    };
    loadUserTypes();
  }, [onRefreshProfiles]);

  // Set permission profiles, individual permissions, and company based on existing data when editing
  useEffect(() => {
    if (selectedUser && availableUserTypes.length > 0) {
      const userTypeIds = selectedUser.userTypes || [];
      setSelectedUserTypes(userTypeIds);

      // If user has System Admin profile, show all permissions as selected
      const hasSystemAdmin = userTypeIds.some((id: number) => {
        const profile = availableUserTypes.find(t => t.id === id);
        return profile && (profile.is_system_profile === 1 || profile.is_system_profile === true)
          && profile.name === 'System Admin';
      });

      setUser({
        ...user,
        user_types: userTypeIds,
        individualPermissions: hasSystemAdmin ? [...availablePermissions] : (selectedUser.individualPermissions || []),
        company_id: selectedUser.company?.id || selectedUser.company_id || '',
        partner_id: selectedUser.partner_id ?? null,
      });
      // Set company search term if company is selected
      if (selectedUser.company?.name) {
        setCompanySearchTerm(selectedUser.company.name);
      } else if (selectedUser.company_id) {
        // Find company name from available companies
        const company = availableCompanies.find(c => c.id === selectedUser.company_id);
        if (company) {
          setCompanySearchTerm(company.name);
        }
      }
    }
  }, [selectedUser, availableUserTypes, availableCompanies]);

  // Profiles visible to the current user when creating/editing:
  // - companyScoped=true (on a company page): only non-system profiles (Company Admin, Compliance User, etc.)
  // - System Admin (not on company page): all profiles
  // - Company Admin: all profiles they have access to
  const visibleUserTypes = useMemo(() => {
    if (companyScoped) {
      // On a company page — hide system-only profiles (System Admin, External Auditor)
      return availableUserTypes.filter(t =>
        !(t.is_system_profile === 1 || t.is_system_profile === true)
      );
    }
    if (isSystemAdmin) {
      // Match what /admin/permission-profiles shows: exclude company-managed profiles
      const COMPANY_MANAGED_PROFILES = ['Internal Auditor', 'Compliance User'];
      return availableUserTypes.filter(t => !COMPANY_MANAGED_PROFILES.includes(t.name));
    }
    return availableUserTypes;
  }, [availableUserTypes, isSystemAdmin, companyScoped]);

  // A profile is truly system-level (no company needed) when is_system_profile = true.
  // Company Admin is company-scoped even though it's a global template.
  const hasStrictlySystemProfile = (typeIds: number[]) =>
    typeIds.some(id => {
      const profile = availableUserTypes.find(t => t.id === id);
      return profile && (profile.is_system_profile === 1 || profile.is_system_profile === true);
    });

  const hasCompanyScopedProfile = (typeIds: number[]) =>
    typeIds.some(id => {
      const profile = availableUserTypes.find(t => t.id === id);
      return profile && !(profile.is_system_profile === 1 || profile.is_system_profile === true);
    });

  // Check if System Admin profile is selected (is_system_profile=1 with id=1 or matching flag)
  const isSystemAdminProfile = (typeIds: number[]) =>
    typeIds.some(id => {
      const profile = availableUserTypes.find(t => t.id === id);
      return profile && (profile.is_system_profile === 1 || profile.is_system_profile === true)
        && profile.name === 'System Admin';
    });

  const isPartnerAdminProfile = (typeIds: number[]) =>
    typeIds.some(id => {
      const profile = availableUserTypes.find(t => t.id === id);
      return profile && profile.name === 'Partner Admin';
    });

  const handleUserTypeChange = (typeIds: number[]) => {
    setSelectedUserTypes(typeIds);

    // Hide company only when ALL selected profiles are strictly system-level.
    // If any company-scoped profile (e.g. Company Admin) is selected, keep/show company field.
    const onlySystemLevel = typeIds.length > 0 && !hasCompanyScopedProfile(typeIds);
    const partnerAdmin = isPartnerAdminProfile(typeIds);

    // When System Admin profile is selected, auto-grant all available permissions
    const autoPermissions = isSystemAdminProfile(typeIds)
      ? [...availablePermissions]
      : user.individualPermissions || [];

    const updatedUser = {
      ...user,
      user_types: typeIds,
      company_id: onlySystemLevel ? null : user.company_id,
      partner_id: partnerAdmin ? user.partner_id : null,
      individualPermissions: autoPermissions,
    };

    if (onlySystemLevel) {
      setCompanySearchTerm('');
    }

    setUser(updatedUser);
  };

  const handleSubmit = async (data: any) => {
    await onSubmit({ ...user, ...data });
    onClose();
  };

  const filteredCompanies = availableCompanies.filter(company =>
    company.name.toLowerCase().includes(companySearchTerm.toLowerCase())
  );

  const isCreate = !selectedUser?.id;

  // Helper function to check if user is protected
  const isUserProtected = (email: string) => {
    return email === 'support@eis-tech.co.uk' || email === 'support@yiontech.co.uk';
  };

  // Helper function for input styling based on readOnly state
  const getInputProps = (baseClassName: string = "w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-all") => ({
    disabled: readOnly,
    className: `${baseClassName} ${readOnly ? 'bg-slate-100 text-slate-500 cursor-not-allowed' : ''}`
  });

  // Special input props for email field - always read-only for protected users
  const getEmailInputProps = (baseClassName: string = "w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-all") => {
    const isProtected = selectedUser && isUserProtected(selectedUser.email);
    return {
      disabled: readOnly || isProtected,
      className: `${baseClassName} ${(readOnly || isProtected) ? 'bg-slate-100 text-slate-500 cursor-not-allowed' : ''}`
    };
  };

  return (
    <FormModal
      title={readOnly ? 'View Protected User' : (isCreate ? 'Create User' : 'Edit User')}
      subtitle={readOnly ? 'This is a protected system user - view only' : (isCreate ? 'Create a new user account' : 'Modify existing user')}
      icon={readOnly ? <Edit className="text-white" size={24} /> : (isCreate ? <Plus className="text-white" size={24} /> : <Edit className="text-white" size={24} />)}
      onSubmit={handleSubmit}
      onCancel={onClose}
      submitText={isCreate ? 'Create User' : 'Update User'}
      cancelText="Close"
      size={size}
      showSubmitButton={!readOnly}
      useFormData={false}
    >
      <div className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <label htmlFor="first_name" className="block text-sm font-medium text-slate-700">
            First Name *
          </label>
          <input
            type="text"
            id="first_name"
            name="first_name"
            required
            value={user.first_name}
            onChange={(e) => setUser({ ...user, first_name: e.target.value })}
            {...getInputProps()}
            placeholder="John"
          />
        </div>
        <div className="space-y-2">
          <label htmlFor="last_name" className="block text-sm font-medium text-slate-700">
            Last Name *
          </label>
          <input
            type="text"
            id="last_name"
            name="last_name"
            required
            value={user.last_name}
            onChange={(e) => setUser({ ...user, last_name: e.target.value })}
            {...getInputProps()}
            placeholder="Doe"
          />
        </div>
      </div>

      <div className="space-y-2">
        <label htmlFor="email" className="block text-sm font-medium text-slate-700">
          Email *
        </label>
        <input
          type="email"
          id="email"
          name="email"
          required
          value={user.email}
          onChange={(e) => setUser({ ...user, email: e.target.value })}
          {...getEmailInputProps()}
          placeholder="john.doe@example.com"
        />
      </div>

      <div className="space-y-2">
        <label htmlFor="user_types" className="block text-sm font-medium text-slate-700">
          Permission Profiles
        </label>
        <select
          id="user_types"
          multiple
          value={selectedUserTypes.map(id => id.toString())}
          onChange={(e) => {
            const selectedOptions = Array.from(e.target.selectedOptions, option => parseInt(option.value));
            handleUserTypeChange(selectedOptions);
          }}
          className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-all bg-white min-h-[120px]"
        >
          {visibleUserTypes.map(userType => (
            <option key={userType.id} value={userType.id.toString()}>
              {userType.name} ({(userType.is_system_profile === 1 || userType.is_system_profile === true) ? 'System' : 'Company'}) - {userType.permissions.length} permissions
            </option>
          ))}
        </select>
        <div className="text-xs text-slate-500 mt-1">
          Hold Ctrl/Cmd to select multiple permission profiles. Each profile grants its associated permissions.
        </div>

        {/* System Admin Warning */}
        {selectedUserTypes.includes(1) && (
          <div className="mt-3 p-4 bg-red-50 border border-red-200 rounded-lg">
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <h3 className="text-sm font-medium text-red-800">
                  ⚠️ System Administrator Role Assignment
                </h3>
                <div className="mt-2 text-sm text-red-700">
                  <p>
                    You are assigning <strong>System Administrator</strong> privileges to this user.
                    This role grants full access to all system functions and data across all companies.
                  </p>
                  <p className="mt-2">
                    <strong>With great power comes great responsibility.</strong> Only assign this role to trusted individuals who require full system access.
                  </p>
                  <p className="mt-2">
                    The user's company association will be automatically removed, and they will operate at the platform level.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Advanced Permissions Override Section */}
      <div className="space-y-2">
        <details className="group" open={isSystemAdminProfile(selectedUserTypes)}>
          <summary className="cursor-pointer text-sm font-medium text-slate-700 hover:text-slate-900 flex items-center justify-between">
            <span>Advanced: Individual Permission Overrides</span>
            <span className="text-xs text-slate-500 group-open:rotate-180 transition-transform">▼</span>
          </summary>
          <div className="mt-3 pt-3 border-t border-slate-200">
            {isSystemAdminProfile(selectedUserTypes) ? (
              <div className="text-xs text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg p-3 mb-3 flex items-center gap-2">
                <svg className="w-4 h-4 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                </svg>
                System Admin profile has all {availablePermissions.length} permissions active.
              </div>
            ) : (
              <div className="text-xs text-slate-600 mb-3">
                Use this section sparingly for exceptional cases. Most permissions should come from permission profiles above.
              </div>
            )}
            <label className="block text-sm font-medium text-slate-700 mb-2">
              Additional Permissions
              {isSystemAdminProfile(selectedUserTypes) && (
                <span className="ml-2 text-xs font-normal text-emerald-600">({availablePermissions.length}/{availablePermissions.length} selected)</span>
              )}
            </label>
            <div className="max-h-40 overflow-y-auto border border-slate-300 rounded-xl p-3 bg-white">
              {availablePermissions && availablePermissions.length > 0 ? (
                availablePermissions.map(permission => (
                  <label key={permission} className="flex items-center space-x-2 mb-2">
                    <input
                      type="checkbox"
                      checked={isSystemAdminProfile(selectedUserTypes) || (user.individualPermissions?.includes(permission) || false)}
                      disabled={isSystemAdminProfile(selectedUserTypes)}
                      onChange={(e) => {
                        if (isSystemAdminProfile(selectedUserTypes)) return;
                        const newPermissions = e.target.checked
                          ? [...(user.individualPermissions || []), permission]
                          : (user.individualPermissions || []).filter((p: string) => p !== permission);
                        setUser({ ...user, individualPermissions: newPermissions });
                      }}
                      className="rounded border-slate-300 text-slate-600 focus:ring-slate-500 disabled:opacity-75"
                    />
                    <span className={`text-sm ${isSystemAdminProfile(selectedUserTypes) ? 'text-emerald-700' : 'text-slate-700'}`}>{permission}</span>
                  </label>
                ))
              ) : (
                <div className="text-sm text-slate-500">Loading permissions...</div>
              )}
            </div>
          </div>
        </details>
      </div>

      {/* System-level profile info box — shown when External Auditor (or similar) is selected, but NOT for Company Admin or Partner Admin */}
      {hasStrictlySystemProfile(selectedUserTypes) && !selectedUserTypes.includes(1) && !isPartnerAdminProfile(selectedUserTypes) && (
        <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
          <div className="flex gap-3">
            <svg className="h-5 w-5 text-blue-400 flex-shrink-0 mt-0.5" viewBox="0 0 20 20" fill="currentColor">
              <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
            </svg>
            <div>
              <h3 className="text-sm font-semibold text-blue-800">System-Level Profile Selected</h3>
              <p className="mt-1 text-sm text-blue-700">
                This user will operate at the platform level with no company affiliation. External Auditors are assigned to specific companies by System Admins from the Companies management page after the user is created.
              </p>
            </div>
          </div>
        </div>
      )}

      {/* Partner dropdown — shown only when Partner Admin profile is selected */}
      {isPartnerAdminProfile(selectedUserTypes) && (
        <div className="space-y-2">
          <label className="block text-sm font-medium text-slate-700">
            Partner Organisation *
          </label>
          {availablePartners.length === 0 ? (
            <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
              No partner organisations found. <a href="/admin/partners" className="underline font-medium">Create one first</a>, then return to add the user.
            </p>
          ) : (
            <select
              value={user.partner_id ?? ''}
              onChange={e => setUser({ ...user, partner_id: e.target.value ? parseInt(e.target.value) : null })}
              className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-violet-500 focus:border-transparent transition-all bg-white"
            >
              <option value="">— Select partner organisation —</option>
              {availablePartners.map(p => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          )}
          <p className="text-xs text-slate-500">Partner admins manage a portfolio of client companies. They do not belong to a single company.</p>
        </div>
      )}

      <div className="space-y-2" style={{ display: (hideCompanySelection || (selectedUserTypes.length > 0 && !hasCompanyScopedProfile(selectedUserTypes))) ? 'none' : 'block' }}>
        <label htmlFor="company_id" className="block text-sm font-medium text-slate-700">
          Company
        </label>
        <div className="relative">
          <input
            type="text"
            placeholder="Search and select company..."
            value={companySearchTerm}
            onChange={(e) => {
              setCompanySearchTerm(e.target.value);
              setShowCompanyDropdown(true);
            }}
            onFocus={() => setShowCompanyDropdown(true)}
            className="w-full px-4 py-3 border border-slate-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-all"
          />
          <button
            type="button"
            className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
            onClick={() => setShowCompanyDropdown(!showCompanyDropdown)}
          >
            ▼
          </button>
          {showCompanyDropdown &&
            <div className="absolute z-10 w-full mt-1 bg-white border border-slate-300 rounded-xl shadow-lg max-h-48 overflow-y-auto">
              {filteredCompanies.map(company => (
                <div
                  key={company.id}
                  className="px-4 py-2 hover:bg-slate-100 cursor-pointer"
                  onClick={() => {
                    setUser({ ...user, company_id: company.id });
                    setCompanySearchTerm(company.name);
                    setShowCompanyDropdown(false);
                  }}
                >
                  {company.name}
                </div>
              ))}
            </div>
          }
        </div>
      </div>
      </div>
    </FormModal>
  );
};